import React from 'react';
import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';

// Cards and tables share one edge: 16px corners and a border, no shadow (a
// page's scroll box would crop a shadow at the corners).
export const cardVariants = cva(
  'relative block rounded-2xl border border-solid text-left text-[inherit] no-underline',
  {
    variants: {
      padding: {
        none: '',
        sm: 'p-5',
        md: 'p-6',
      },
      /** highlight: has changes to review; muted: archived or not available */
      tone: {
        default: 'bg-white',
        highlight: 'bg-orange-50',
        muted: 'bg-navy-50',
      },
      selected: {
        true: 'border-k-orange',
        false: 'border-k-gray',
      },
      interactive: {
        true: 'cursor-pointer transition-colors hover:border-navy-200',
        false: '',
      },
    },
    compoundVariants: [
      { selected: true, interactive: true, class: 'hover:border-k-orange' },
    ],
    defaultVariants: {
      padding: 'md',
      tone: 'default',
      selected: false,
      interactive: false,
    },
  },
);

type KoobCardProps = Omit<React.HTMLAttributes<HTMLDivElement>, 'className'> &
  VariantProps<typeof cardVariants> & {
    /** Render the child (a Link, a button) as the card. */
    asChild?: boolean;
  };

const KoobCard = React.forwardRef<HTMLDivElement, KoobCardProps>(
  (
    {
      padding,
      tone,
      selected,
      asChild = false,
      interactive,
      children,
      ...props
    },
    ref,
  ) => {
    const Comp = asChild ? Slot : 'div';
    return (
      <Comp
        ref={ref}
        className={cardVariants({
          padding,
          tone,
          selected,
          // Links, buttons and clickable cards get the hover border.
          interactive: interactive ?? (asChild || Boolean(props.onClick)),
        })}
        {...props}
      >
        {children}
      </Comp>
    );
  },
);

KoobCard.displayName = 'KoobCard';

export default KoobCard;
